import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import "./TopProgressActivitiesPage.css";

export default function TopProgressActivitiesPage() {
  const { userId } = useParams();

  const activities = useQuery(
    api.progress.getTopProgressActivities,
    userId ? { userId } : "skip"
  );

  // -----------------------------
  // LOADING
  // -----------------------------
  if (activities === undefined) {
    return (
      <div className="top-progress-page">
        <div className="top-progress-container">
          <div className="page-header">
            <div className="page-badge">
              PROGRESS TRACKER
            </div>

            <h1>Top Progress Activities</h1>

            <p>
              Loading your activities...
            </p>
          </div>

          <div className="loading-card">
            <div className="loading-spinner"></div>

            <p>Loading your activities...</p>
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------
  // EMPTY
  // -----------------------------
  if (!activities || activities.length === 0) {
    return (
      <div className="top-progress-page">
        <div className="top-progress-container">

          <div className="page-header">
            <div className="page-badge">
              PROGRESS TRACKER
            </div>

            <h1>
              Top Progress Activities
            </h1>

            <p>
              Your highest-scoring activities will appear here.
            </p>
          </div>

          <div className="empty-activities-card">
            <div className="empty-icon">
              ✦
            </div>

            <h2>
              No activities yet
            </h2>

            <p>
              Complete some activities to start building your
              progress ranking.
            </p>
          </div>

        </div>
      </div>
    );
  }

  // -----------------------------
  // SORT ACTIVITIES
  // -----------------------------
  const sortedActivities = [...activities].sort(
    (a, b) =>
      Number(b.score ?? 0) -
      Number(a.score ?? 0)
  );

  const top10 = sortedActivities.slice(0, 10);

  // -----------------------------
  // CHART DATA
  // -----------------------------
  const chartData = top10.map(
    (activity, index) => ({
      id: activity._id,
      activity: `${index + 1}. ${activity.activity}`,
      score: Math.min(
        Math.max(
          Number(activity.score ?? 0),
          0
        ),
        100
      ),
      rank: index + 1,
    })
  );

  return (
    <div className="top-progress-page">
      <div className="top-progress-container">

        {/* =========================
            HEADER
        ========================== */}

        <div className="page-header">
          <div className="page-badge">
            PROGRESS TRACKER
          </div>

          <h1>
            Top Progress Activities
          </h1>

          <p>
            Your 10 highest-scoring activities.
          </p>
        </div>


        {/* =========================
            CHART
        ========================== */}

        <section className="performance-section">

          <div className="section-heading">
            <div>
              <span className="section-label">
                PERFORMANCE
              </span>

              <h2>
                Activity Performance
              </h2>
            </div>

            <div className="score-range">
              0 — 100
            </div>
          </div>

          <div className="chart-wrapper">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{
                  top: 10,
                  right: 30,
                  left: 20,
                  bottom: 10,
                }}
              >

                {/* GRID */}

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#263244"
                  horizontal={false}
                />

                {/* SCORE AXIS */}

                <XAxis
                  type="number"
                  domain={[0, 100]}
                  allowDecimals={false}
                  tickCount={11}
                  stroke="#64748b"
                  tick={{
                    fill: "#64748b",
                    fontSize: 12,
                  }}
                />

                {/* ACTIVITY AXIS */}

                <YAxis
                  type="category"
                  dataKey="activity"
                  width={210}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "#cbd5e1",
                    fontSize: 13,
                  }}
                />

                {/* TOOLTIP */}

                <Tooltip
                  cursor={{
                    fill: "rgba(99, 102, 241, 0.06)",
                  }}
                  contentStyle={{
                    backgroundColor: "#111827",
                    border: "1px solid #334155",
                    borderRadius: "12px",
                    color: "#ffffff",
                    boxShadow:
                      "0 12px 30px rgba(0,0,0,0.3)",
                  }}
                  labelStyle={{
                    color: "#cbd5e1",
                    marginBottom: "5px",
                  }}
                  formatter={(value) => [
                    `${value}`,
                    "Score",
                  ]}
                />

                {/* BARS */}

                <Bar
                  dataKey="score"
                  name="Score"
                  barSize={30}
                  radius={[0, 7, 7, 0]}
                  fill="#6366f1"
                />

              </BarChart>
            </ResponsiveContainer>
          </div>

        </section>


        {/* =========================
            TOP 10 LIST
        ========================== */}

        <section className="top-ten-section">

          <div className="top-ten-heading">
            <div>
              <span className="section-label">
                RANKING
              </span>

              <h2>
                Top 10
              </h2>
            </div>

            <span className="activity-count">
              {top10.length} activities
            </span>
          </div>


          <div className="activity-list">

            {top10.map((activity, index) => {

              const score = Math.min(
                Math.max(
                  Number(activity.score ?? 0),
                  0
                ),
                100
              );

              return (
                <div
                  key={activity._id}
                  className="activity-card"
                >

                  {/* RANK */}

                  <div className="rank-container">
                    <div className="rank-number">
                      {index + 1}
                    </div>
                  </div>


                  {/* ACTIVITY */}

                  <div className="activity-info">

                    <div className="activity-name-row">

                      <div>
                        <h3>
                          {activity.activity}
                        </h3>

                        <p>
                          {activity.category}
                        </p>
                      </div>

                    </div>


                    {/* PROGRESS BAR */}

                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${score}%`,
                        }}
                      />
                    </div>

                  </div>


                  {/* SCORE */}

                  <div className="score-container">

                    <p>
                      {score}
                    </p>

                    <span>
                      Score
                    </span>

                  </div>

                </div>
              );
            })}

          </div>

        </section>

      </div>
    </div>
  );
}


